import React, { useMemo, useState } from "react";
import {
  Box,
  InputAdornment,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import CurrencySymbol from "../../../Components/common/CurrencySymbol";
import WidgetShell from "./WidgetShell";
import {
  EXD,
  ExdChip,
  ExdEmpty,
  ExdListRow,
  ExdStatItem,
  scrollbarSx,
} from "./exdMui";

const StationUtilizationListWidget = ({
  loading,
  translate,
  topologyData,
  loginUserCurrency,
  infoTitle,
}) => {
  const [query, setQuery] = useState("");

  const rows = useMemo(() => {
    const list = Array.isArray(topologyData) ? [...topologyData] : [];
    return list.sort(
      (a, b) => (Number(b.servedCount) || 0) - (Number(a.servedCount) || 0),
    );
  }, [topologyData]);

  const filteredRows = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return rows;
    return rows.filter((item) => {
      const hay = [item.stationName, item.area, item.type, item.city]
        .filter(Boolean)
        .join(" ")
        .toLowerCase();
      return hay.includes(q);
    });
  }, [rows, query]);

  const count = filteredRows.length;
  const totalWos = useMemo(
    () =>
      filteredRows.reduce(
        (sum, item) => sum + (Number(item.servedCount) || 0),
        0,
      ),
    [filteredRows],
  );

  const chipTone = (wos) => {
    if (wos >= 50) return "good";
    if (wos >= 5) return "neutral";
    return "warn";
  };

  return (
    <WidgetShell
      title={translate("Vehicle Service Utilization by Station")}
      infoTitle={infoTitle}
      loading={loading}
      summary={
        <Stack
          direction="row"
          alignItems="center"
          spacing={1}
          sx={{ flexWrap: "nowrap", minWidth: 0, width: "100%" }}
        >
          <Stack
            direction="row"
            alignItems="center"
            spacing={1.5}
            sx={{ flex: 1, flexWrap: "nowrap", minWidth: 0, overflow: "hidden" }}
          >
            <ExdStatItem label="Locations" compact>
              {count}
              {query.trim() ? ` / ${rows.length}` : ""}
            </ExdStatItem>
            <ExdStatItem label="Work orders" compact>
              {totalWos.toLocaleString()}
            </ExdStatItem>
          </Stack>

          <TextField
            size="small"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder={translate("search") || "Search"}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon sx={{ fontSize: 14, color: EXD.muted }} />
                </InputAdornment>
              ),
            }}
            sx={{
              flex: "none",
              width: 120,
              "& .MuiOutlinedInput-root": {
                bgcolor: EXD.surface2,
                borderRadius: "7px",
                fontSize: 11,
                height: 26,
                "& fieldset": { borderColor: EXD.border },
                "&:hover fieldset": { borderColor: EXD.accent },
                "&.Mui-focused fieldset": {
                  borderColor: EXD.accent,
                  borderWidth: 1,
                },
              },
              "& .MuiOutlinedInput-input": {
                py: 0.25,
                px: 0.25,
              },
            }}
          />
        </Stack>
      }
    >
      <Box sx={{ maxHeight: 340, overflowY: "auto", pr: 0.5, ...scrollbarSx }}>
        {filteredRows.length ? (
          filteredRows.map((item, index) => {
            const wos = Math.floor(Number(item.servedCount) || 0);
            const sub =
              item.bays || item.bayCount
                ? `${item.bays || item.bayCount} bays`
                : [item.type, item.area].filter(Boolean).join(" · ");

            return (
              <Tooltip
                key={item.garageId || `${item.stationName}-${index}`}
                title={
                  <Typography>
                    {translate("stationName")} {""}: {item.stationName} <br />
                    {translate("area")} {""}: {item.area} <br />
                    {translate("city")} {""}: {item.type} <br />
                    {translate("Total Vehicles")} {""}: {item.servedCount}{" "}
                    <br />
                    {translate("Total Revenue")} {""}:{" "}
                    {item.totalSale
                      ? Number(item.totalSale).toLocaleString("en-US", {
                          minimumFractionDigits: 2,
                          maximumFractionDigits: 2,
                        })
                      : 0}{" "}
                    &nbsp;&nbsp;{" "}
                    <CurrencySymbol
                      currency={loginUserCurrency}
                      withParentheses={false}
                    />
                  </Typography>
                }
                placement="bottom-start"
                enterDelay={300}
              >
                <Box>
                  <ExdListRow
                    rank={String(index + 1).padStart(2, "0")}
                    name={item.stationName}
                    sub={sub}
                    trailing={
                      <ExdChip tone={chipTone(wos)}>{wos} WOs</ExdChip>
                    }
                  />
                </Box>
              </Tooltip>
            );
          })
        ) : (
          <ExdEmpty>{translate("No Data") || "No data"}</ExdEmpty>
        )}
      </Box>
    </WidgetShell>
  );
};

export default StationUtilizationListWidget;
